import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Paper, 
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio
} from '@mui/material';

// Utility function to capitalize first letter of each word
const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Utility function to format hashtags
const formatHashtags = (tags) => {
  if (!tags || !tags.trim()) return '';
  
  // Split by comma and clean up each tag
  const tagArray = tags.split(',').map(tag => {
    let cleanTag = tag.trim();
    // Remove any existing # symbols
    cleanTag = cleanTag.replace(/^#+/, '');
    // Add # symbol if tag is not empty
    return cleanTag ? `#${cleanTag}` : '';
  }).filter(tag => tag !== '');
  
  // Join with space
  return tagArray.join(' ');
};

export default function Article() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [video, setVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [fetchingCategories, setFetchingCategories] = useState(true);
  
  // New form fields for news type and location
  const [newsType, setNewsType] = useState(1); // 1: National, 2: State, 3: District, 4: Constituency
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [isBreakingNews, setIsBreakingNews] = useState(false);
  const [tags, setTags] = useState('');
  
  // States, districts, and constituencies data
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [fetchingStates, setFetchingStates] = useState(true);
  const [fetchingDistricts, setFetchingDistricts] = useState(false);
  const [fetchingConstituencies, setFetchingConstituencies] = useState(false);
  
  // Form fields for API
  const [postType, setPostType] = useState(1); // 1: News, 2: Image Post
  const [languageId] = useState('L100');
  const [latitude] = useState(73.90);
  const [longitude] = useState(78.90);

  // Get session data from localStorage
  const getSessionData = () => {
    const sessionId = localStorage.getItem('sessionId');
    const dashAdminId = localStorage.getItem('dashAdminId');
    
    console.log('🔍 Session data check for Article:', {
      sessionId: sessionId ? 'Found' : 'Not found',
      dashAdminId: dashAdminId ? 'Found' : 'Not found',
      sessionIdValue: sessionId,
      dashAdminIdValue: dashAdminId
    });
    
    return { sessionId, dashAdminId };
  };

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for categories');
      setFetchingCategories(false);
      return;
    }

    setFetchingCategories(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isSearchFilter: false,
          search: "",
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Categories fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort categories by sNo for ranking
          const sortedCategories = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999; // Default to high number if sNo is missing
            const bSNo = b.sNo || 999;
            return aSNo - bSNo; // Sort in ascending order
          });
          setCategories(sortedCategories);
        } else {
          setCategories([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching categories:', errorData);
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setFetchingCategories(false);
    }
  }, []);

  // Fetch states from API
  const fetchStates = useCallback(async () => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for states');
      setFetchingStates(false);
      return;
    }

    setFetchingStates(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          islanguageIdFilter: false,
          languageId: "",
          isSearchFilter: false,
          search: "",
          sortBy: 'sNo', // Sort by sNo for ranking
          sortOrder: 'asc' // Ascending order for ranking
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('States fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          // Sort states by sNo for ranking
          const sortedStates = result.data.sort((a, b) => {
            const aSNo = a.sNo || 999; // Default to high number if sNo is missing
            const bSNo = b.sNo || 999;
            return aSNo - bSNo; // Sort in ascending order
          });
          setStates(sortedStates);
        } else {
          setStates([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching states:', errorData);
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setFetchingStates(false);
    }
  }, []);

  // Fetch districts from API
  const fetchDistricts = useCallback(async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for districts');
      setFetchingDistricts(false);
      return;
    }

    setFetchingDistricts(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: true,
          stateId: stateId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Districts fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setDistricts(result.data);
        } else {
          setDistricts([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching districts:', errorData);
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setFetchingDistricts(false);
    }
  }, []);

  // Fetch constituencies from API
  const fetchConstituencies = useCallback(async (districtId) => {
    if (!districtId) {
      setConstituencies([]);
      return;
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      console.error('❌ Session data not found for constituencies');
      setFetchingConstituencies(false);
      return;
    }

    setFetchingConstituencies(true);
    try {
      const response = await fetch('https://api.jpnewz.com/dashboard/list_all_constituencies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          dashAdminId,
          skip: 0,
          limit: 100,
          isActiveFilter: true,
          isActive: true,
          isstateIdFilter: false,
          stateId: "",
          isdistrictIdFilter: true,
          districtId: districtId,
          isSearchFilter: false,
          search: ""
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Constituencies fetched:', result);
        if (result.data && Array.isArray(result.data)) {
          setConstituencies(result.data);
        } else {
          setConstituencies([]);
        }
      } else {
        const errorData = await response.json();
        console.error('Error fetching constituencies:', errorData);
        setConstituencies([]);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
    } finally {
      setFetchingConstituencies(false);
    }
  }, []);

  // Handle news type change
  const handleNewsTypeChange = (event) => {
    const newNewsType = parseInt(event.target.value);
    setNewsType(newNewsType);
    
    // Reset state, district, and constituency selections when news type changes
    if (newNewsType === 1) { // National
      setSelectedState('');
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 2) { // State
      setSelectedDistrict('');
      setSelectedConstituency('');
      setDistricts([]);
      setConstituencies([]);
    } else if (newNewsType === 3) { // District
      setSelectedConstituency('');
      setConstituencies([]);
    } else if (newNewsType === 4) { // Constituency
      // Keep state and district, but reset constituency
      setSelectedConstituency('');
      setConstituencies([]);
    }
  };

  // Handle state change
  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedState(stateId);
    setSelectedDistrict(''); // Reset district when state changes
    setSelectedConstituency(''); // Reset constituency when state changes
    
    if (stateId && (newsType === 3 || newsType === 4)) { // Fetch districts if news type is District or Constituency
      fetchDistricts(stateId);
    } else {
      setDistricts([]);
    }
    setConstituencies([]);
  };

  // Handle district change
  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedConstituency(''); // Reset constituency when district changes
    
    if (districtId && newsType === 4) { // Only fetch constituencies if news type is Constituency
      fetchConstituencies(districtId);
    } else {
      setConstituencies([]);
    }
  };

  // Fetch categories and states on component mount
  useEffect(() => {
    fetchCategories();
    fetchStates();
  }, [fetchCategories, fetchStates]);

  // Upload image function
  const uploadImage = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading image with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    const response = await fetch('https://api.jpnewz.com/upload/upload_image', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    console.log('📥 Upload image response:', result);

    if (!response.ok) {
      console.error('❌ Upload failed with status:', response.status);
      throw new Error(result.message || `Upload failed with status ${response.status}`);
    }

    if (!result.status) {
      console.error('❌ Upload failed with result:', result);
      throw new Error(result.message || 'Upload failed');
    }

    if (!result.data) {
      console.error('❌ Upload response missing data:', result);
      throw new Error('Upload response missing data');
    }

    if (!result.data._id) {
      console.error('❌ Upload response missing _id:', result.data);
      throw new Error('Upload response missing image ID');
    }

    console.log('✅ Upload successful, image ID:', result.data._id);
    return result.data;
  };

  // Upload video function
  const uploadVideo = async (file) => {
    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      throw new Error('Session data not found');
    }

    const formData = new FormData();
    formData.append('routeType', '1');
    formData.append('file', file);
    formData.append('dashAdminId', dashAdminId);
    formData.append('sessionId', sessionId);

    console.log('📤 Uploading video with FormData:', {
      routeType: '1',
      dashAdminId,
      sessionId,
      fileName: file.name,
      fileSize: file.size
    });

    try {
      const response = await fetch('https://api.jpnewz.com/upload/upload_video', {
        method: 'POST',
        body: formData,
      });

      console.log('📥 Upload video response status:', response.status);

      if (!response.ok) {
        console.error('❌ Video upload failed with status:', response.status);
        
        // Handle 413 specifically
        if (response.status === 413) {
          throw new Error(`Video file is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Please compress the video or use a smaller file.`);
        }
        
        // Try to get error message from response
        let errorMessage = `Video upload failed with status ${response.status}`;
        try {
          const errorResult = await response.json();
          errorMessage = errorResult.message || errorMessage;
        } catch (e) {
          console.log('Could not parse error response as JSON');
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('📥 Upload video response:', result);

      if (!result.status) {
        console.error('❌ Video upload failed with result:', result);
        throw new Error(result.message || 'Video upload failed');
      }

      if (!result.data) {
        console.error('❌ Video upload response missing data:', result);
        throw new Error('Video upload response missing data');
      }

      if (!result.data._id) {
        console.error('❌ Video upload response missing _id:', result.data);
        throw new Error('Video upload response missing video ID');
      }

      console.log('✅ Video upload successful, video ID:', result.data._id);
      return result.data;
      
    } catch (error) {
      console.error('❌ Video upload error:', error);
      
      // Handle network/CORS errors
      if (error.name === 'TypeError' && error.message.includes('Load failed')) {
        throw new Error(`Video upload failed. The file might be too large (${(file.size / (1024 * 1024)).toFixed(1)}MB) or there's a network issue. Please try a smaller file.`);
      }
      
      // Re-throw other errors
      throw error;
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setError('');
      
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImageUrl('');
    setError('');
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideo(file);
      setError('');
      
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setVideoUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    setVideo(null);
    setVideoUrl('');
    setError('');
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][accept="video/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    // Description is required for News (postType: 1), optional for Image Post (postType: 2)
    if (postType === 1 && !description.trim()) {
      setError('Please enter a description');
      return;
    }
    // For Image Post, image is required
    if (postType === 2 && !image) {
      setError('Please select an image for Image Post');
      return;
    }
    // For News, either image or video is required
    if (postType === 1 && !image && !video) {
      setError('Please select either an image or video');
      return;
    }
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    // Validate news type specific requirements - Only for News (postType: 1)
    if (postType === 1) {
      if (newsType === 2 && !selectedState) { // State news requires state selection
        setError('Please select a state for state-level news');
        return;
      }
      if (newsType === 3 && !selectedState) { // District news requires state selection
        setError('Please select a state for district-level news');
        return;
      }
      if (newsType === 3 && !selectedDistrict) { // District news requires district selection
        setError('Please select a district for district-level news');
        return;
      }
      if (newsType === 4 && !selectedState) { // Constituency news requires state selection
        setError('Please select a state for constituency-level news');
        return;
      }
      if (newsType === 4 && !selectedDistrict) { // Constituency news requires district selection
        setError('Please select a district for constituency-level news');
        return;
      }
      if (newsType === 4 && !selectedConstituency) { // Constituency news requires constituency selection
        setError('Please select a constituency for constituency-level news');
        return;
      }
    }

    const { sessionId, dashAdminId } = getSessionData();
    
    if (!sessionId || !dashAdminId) {
      setError('Session data not found. Please login again.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let uploadResult = null;
      let mediaType = 1; // Default to image
      let mediaArray = [];

      // Upload image or video first
      if (image) {
        setUploadingImage(true);
        console.log('🔄 Starting image upload for file:', image.name);
        uploadResult = await uploadImage(image);
        
        console.log('✅ Image uploaded successfully:', {
          imageId: uploadResult._id,
          imageUrl: uploadResult.s3FileURL
        });
        setUploadingImage(false);
        
        mediaType = 1; // Image
        mediaArray = [
          {
            mediaType: 1,
            imageId: uploadResult._id,
            videoId: null
          }
        ];
      } else if (video) {
        setUploadingVideo(true);
        console.log('🔄 Starting video upload for file:', video.name);
        uploadResult = await uploadVideo(video);
        
        console.log('✅ Video uploaded successfully:', {
          videoId: uploadResult._id,
          videoUrl: uploadResult.s3FileURL
        });
        setUploadingVideo(false);
        
        mediaType = 2; // Video
        mediaArray = [
          {
            mediaType: 2,
            imageId: null,
            videoId: uploadResult._id
          }
        ];
      }

      // Now create the post
      const requestBody = {
        sessionId,
        dashAdminId,
        postType,
        languageId,
        newsType: postType === 2 ? 1 : newsType, // Default to 1 (National) for Image Post
        title: title.trim(),
        description: postType === 2 ? '' : description.trim(), // Empty for Image Post
        categoryId: selectedCategory,
        stateId: postType === 2 ? null : (newsType === 1 ? null : selectedState), // null for Image Post and national news
        districtId: postType === 2 ? null : ((newsType === 3 || newsType === 4) ? selectedDistrict : null), // null for Image Post
        constituencyId: postType === 2 ? null : (newsType === 4 ? selectedConstituency : null), // null for Image Post
        latitude,
        longitude,
        hashTags: postType === 2 ? '' : formatHashtags(tags), // Empty for Image Post
        expiryTime: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        colorCode: null,
        isBreakingNews: postType === 2 ? false : isBreakingNews, // Not required for Image Post, set to false
        mediaArray
      };

      console.log('📤 Sending create post request:', requestBody);

      const response = await fetch('https://api.jpnewz.com/dashboard/create_post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();
      console.log('📥 Create post response:', result);

      if (response.ok && result.status === true) {
        setSuccess('Post created successfully!');
        // Reset form
        setTitle('');
        setDescription('');
        setImage(null);
        setImageUrl('');
        setVideo(null);
        setVideoUrl('');
        setSelectedCategory('');
        setNewsType(1);
        setSelectedState('');
        setSelectedDistrict('');
        setSelectedConstituency('');
        setIsBreakingNews(false);
        setDistricts([]);
        setConstituencies([]);
        setTags('');
        setPostType(1); // Reset to News
      } else {
        setError(`Error: ${result.message || 'Failed to create post'}`);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
      setUploadingImage(false);
      setUploadingVideo(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '90vh', gap: 3 }}>
      {/* Left: Form */}
      <Paper elevation={3} sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>Create Post</Typography>
        
        {/* Post Type Radio Buttons */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            Post Type *
          </Typography>
          <RadioGroup
            row
            value={postType}
            onChange={(e) => {
              const newPostType = parseInt(e.target.value);
              setPostType(newPostType);
              // Reset form fields when switching post types
              if (newPostType === 2) {
                // Image Post: clear video, make description optional
                setVideo(null);
                setVideoUrl('');
                setIsBreakingNews(false);
              }
            }}
          >
            <FormControlLabel value={1} control={<Radio />} label="News" />
            <FormControlLabel value={2} control={<Radio />} label="Image Post" />
          </RadioGroup>
        </FormControl>
        
        {/* Error and Success Messages */}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Category Dropdown */}
        <FormControl fullWidth>
          <InputLabel>Category *</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category *"
            disabled={fetchingCategories}
          >
            {categories.map((category) => (
              <MenuItem key={category.categoryId} value={category.categoryId}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* News Type Dropdown - Only show for News (postType: 1) */}
        {postType === 1 && (
          <FormControl fullWidth>
            <InputLabel>News Type *</InputLabel>
            <Select
              value={newsType}
              onChange={handleNewsTypeChange}
              label="News Type *"
            >
              <MenuItem value={1}>National</MenuItem>
              <MenuItem value={2}>State</MenuItem>
              <MenuItem value={3}>District</MenuItem>
              <MenuItem value={4}>Constituency</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* State Dropdown - Only show for News (postType: 1) with State, District, and Constituency news */}
        {postType === 1 && (newsType === 2 || newsType === 3 || newsType === 4) && (
          <FormControl fullWidth>
            <InputLabel>State *</InputLabel>
            <Select
              value={selectedState}
              onChange={handleStateChange}
              label="State *"
              disabled={fetchingStates}
            >
              {states.map((state) => (
                <MenuItem key={state.stateId} value={state.stateId}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* District Dropdown - Only show for News (postType: 1) with District and Constituency news */}
        {postType === 1 && (newsType === 3 || newsType === 4) && (
          <FormControl fullWidth>
            <InputLabel>District *</InputLabel>
            <Select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              label="District *"
              disabled={fetchingDistricts || !selectedState}
            >
              {districts.map((district) => (
                <MenuItem key={district.districtId} value={district.districtId}>
                  {district.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Constituency Dropdown - Only show for News (postType: 1) with Constituency news */}
        {postType === 1 && newsType === 4 && (
          <FormControl fullWidth>
            <InputLabel>Constituency *</InputLabel>
            <Select
              value={selectedConstituency}
              onChange={(e) => setSelectedConstituency(e.target.value)}
              label="Constituency *"
              disabled={fetchingConstituencies || !selectedDistrict}
            >
              {constituencies.map((constituency) => (
                <MenuItem key={constituency.constituencyId} value={constituency.constituencyId}>
                  {constituency.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Breaking News Checkbox - Only show for News (postType: 1) */}
        {postType === 1 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isBreakingNews}
                onChange={(e) => setIsBreakingNews(e.target.checked)}
                sx={{
                  color: '#1976d2',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />
            }
            label="Breaking News"
            sx={{ 
              color: '#1976d2', 
              fontWeight: 500,
              '& .MuiFormControlLabel-label': {
                fontWeight: 500
              }
            }}
          />
        )}


        {/* Title */}
        <TextField
          label="Title *"
          value={title}
          onChange={e => setTitle(capitalizeFirstLetter(e.target.value))}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '12px 8px 12px 8px', // Internal padding for input text
            },
            '& .MuiInputLabel-root': {
              padding: '0 8px', // Label padding
            }
          }}
        />
        
        {/* Description - Only show for News (postType: 1) */}
        {postType === 1 && (
          <Box>
            <TextField
              label="Description *"
              value={description}
              onChange={e => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              required
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Word count: {description.trim() ? description.trim().split(/\s+/).filter(word => word.length > 0).length : 0}
            </Typography>
          </Box>
        )}

        {/* Tags - Only show for News (postType: 1) */}
        {postType === 1 && (
          <TextField
            label="Tags"
            value={tags}
            onChange={e => setTags(e.target.value)}
            fullWidth
            placeholder="Enter hashtags separated by commas (e.g., Vote Chori, Rahul Gandhi, Politics)"
            helperText="Add relevant hashtags to categorize your article"
          />
        )}

        {/* Media Upload Section */}
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {postType === 1 ? "Article Media *" : "Image Upload *"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {postType === 1 
              ? "Choose either an image or video for your article"
              : "Upload an image for your post"}
          </Typography>
          
          {/* Image Upload */}
          <Box sx={{ mb: postType === 1 ? 3 : 0 }}>
            <Typography variant="subtitle2" gutterBottom>Image Upload</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Supported formats: JPG, PNG, GIF
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                component="label"
                sx={{ width: 'fit-content' }}
                disabled={postType === 1 ? !!video : false}
              >
                Select Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {imageUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRemoveImage}
                  sx={{ width: 'fit-content' }}
                >
                  Remove Image
                </Button>
              )}
            </Box>
            {imageUrl && (
              <Box sx={{ mb: 2 }}>
                <img src={imageUrl} alt="Preview" style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} />
                <Typography variant="caption" color="info.main" display="block">
                  ✓ Image selected (will upload when you submit)
                </Typography>
              </Box>
            )}
          </Box>

          {/* Video Upload - Only show for News (postType: 1) */}
          {postType === 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Video Upload</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Supported formats: MP4, AVI, MOV, WMV
              </Typography>
              <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
                ⚠️ Large video files may take longer to upload or fail. Consider compressing large videos.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  component="label"
                  sx={{ width: 'fit-content' }}
                  disabled={!!image}
                >
                  Select Video
                  <input
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={handleVideoChange}
                  />
                </Button>
                {videoUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveVideo}
                    sx={{ width: 'fit-content' }}
                  >
                    Remove Video
                  </Button>
                )}
              </Box>
              {videoUrl && (
                <Box sx={{ mb: 2 }}>
                  <video 
                    src={videoUrl} 
                    controls 
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }} 
                  />
                  <Typography variant="caption" color="info.main" display="block">
                    ✓ Video selected (will upload when you submit)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting || 
            !title.trim() || 
            (postType === 1 && !description.trim()) || 
            (postType === 1 && !image && !video) ||
            (postType === 2 && !image) ||
            !selectedCategory ||
            (postType === 1 && newsType === 2 && !selectedState) ||
            (postType === 1 && newsType === 3 && (!selectedState || !selectedDistrict)) ||
            (postType === 1 && newsType === 4 && (!selectedState || !selectedDistrict || !selectedConstituency))
          }
          sx={{ 
            mt: 2, 
            bgcolor: '#1976d2',
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              {uploadingImage ? 'Uploading Image...' : uploadingVideo ? 'Uploading Video...' : 'Creating Post...'}
            </>
          ) : (
            postType === 1 ? 'Submit Article' : 'Submit Image Post'
          )}
        </Button>
      </Paper>

      {/* Right: Mobile Preview */}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        <Paper
          elevation={6}
          sx={{
            width: 280,
            height: 500,
            borderRadius: '24px',
            overflow: 'hidden',
            bgcolor: '#fafbfc',
            border: '1px solid #e0e0e0',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ bgcolor: '#1976d2', color: '#fff', p: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2">News Preview</Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Media: Top 50% */}
            <Box sx={{ height: '50%', width: '100%', bgcolor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imageUrl ? (
                <img src={imageUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : videoUrl ? (
                <video 
                  src={videoUrl} 
                  controls 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                <Typography color="text.secondary">Media Preview</Typography>
              )}
            </Box>
            {/* Title: Next 15% */}
            <Box sx={{ height: '5%', width: '100%', display: 'flex', alignItems: 'center', px: 1, py: 2, borderBottom: '1px solid #eee', borderTop: '1px solid #eee', bgcolor: '#fff' }}>
              <Typography variant="h6" fontWeight={700} sx={{ width: '100%', textAlign: 'left', fontSize: 18, wordBreak: 'break-word', lineHeight: 1.3 }}>
                {title || <span style={{ color: '#bbb' }}>Title goes here</span>}
              </Typography>
            </Box>
            {/* Description: Bottom 40% */}
            <Box sx={{ height: '40%', width: '100%', p: 2, bgcolor: '#fafbfc', overflow: 'hidden' }}>
              <Typography variant="body1" sx={{ color: '#444', fontSize: 15, wordBreak: 'break-word', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical' }}>
                {description || <span style={{ color: '#bbb' }}>Description will appear here</span>}
              </Typography>
              {tags && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500, display: 'block', mb: 0.5 }}>
                    Tags:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontSize: 12, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {formatHashtags(tags)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
} 